import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UI, DISPLAY } from '../styles/tl5';
import renderLegalMarkdown, { GRIEVANCE_MARKER } from '../utils/legalMarkdown';
import {
  useGetPrivacyPolicyQuery,
  useGetTermsOfServiceQuery,
  useGetContactInfoQuery,
} from '../store/siteContentApi';

// Legal — privacy, terms and refunds in one page, built to the v5
// reference: eyebrow + title, three tabs, an article column and a sticky
// "On this page" aside.
//
// The reference ships ~900 lines of its own policy text and closes the
// aside with "Draft written for the website prototype. Please have it
// reviewed by your legal counsel before publishing." None of that text is
// used here. Our policies are real, published and admin-editable, and
// quietly replacing them with a prototype draft is the one change on this
// page that could actually hurt someone. So the reference supplies the
// layout and we supply the words:
//
//   · Privacy  → GET /legal/privacy, already 15 ordered sections with
//                markdown bodies, which is the exact shape the reference
//                draws. Rendered through the existing legalMarkdown
//                renderer, grievance card and all.
//   · Terms    → GET /legal/terms, a single markdown blob, split on its
//                `## ` headings into the same numbered shape.
//   · Refunds  → there is no refunds document. The reference invents one.
//                This tab shows the real "Refunds & cancellations" section
//                out of the Terms and says that is where it comes from,
//                rather than publishing a second, differently-worded
//                refund policy. See API-NOTES 2.23.
//
// The reference's disclaimer line is dropped for the same reason: it would
// be telling readers that TrustLab's live privacy policy is an unreviewed
// prototype, which is not true.

const TABS = [
  { key: 'privacy', label: 'Privacy policy' },
  { key: 'terms', label: 'Terms' },
  { key: 'refunds', label: 'Refunds' },
];

// Our terms arrive as one markdown document. Everything before the first
// `## ` is the preamble; each `## ` after that becomes a numbered section,
// so terms and privacy render through the same component.
const splitTermsMarkdown = (md) => {
  if (!md) return { intro: '', sections: [] };
  const lines = String(md).split('\n');
  const intro = [];
  const sections = [];
  let current = null;
  lines.forEach((line) => {
    const h2 = /^##\s+(.*)$/.exec(line);
    if (h2) {
      current = { title: h2[1].trim(), body: [] };
      sections.push(current);
      return;
    }
    // Drop the document's own `# ` title — the page already has an <h1>.
    if (/^#\s+/.test(line)) return;
    if (current) current.body.push(line);
    else intro.push(line);
  });
  return {
    intro: intro.join('\n').trim(),
    sections: sections.map((s, i) => ({
      slug: s.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      number: String(i + 1),
      title: s.title,
      content_md: s.body.join('\n').trim(),
    })),
  };
};

const REFUND_SECTION = 'refunds-cancellations';

// The privacy policy stores its numbers as "SECTION 01". The reference
// prints a bare "1." before the title, so pull the digits out and drop
// the padding zero. Anything that isn't in that shape passes through.
const sectionNumber = (n) => {
  const digits = /(\d+)/.exec(String(n ?? ''));
  return digits ? String(Number(digits[1])) : String(n ?? '');
};

// ── shared fragments ──────────────────────────────────────────────────
const panel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const sectionTitle = {
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 22,
  lineHeight: 1.15,
  letterSpacing: '-.01em',
  color: 'var(--tl-g9)',
};

// The admin writes markdown; this is the house style the rendered HTML
// lands in. Kept as one sx object so there is no stylesheet to add.
const prose = {
  fontFamily: UI,
  color: 'var(--tl-muted)',
  minWidth: 0,
  // The admin's markdown can carry a long email, URL or document id that
  // no column is wide enough for.
  overflowWrap: 'anywhere',
  '& p': { m: 0, mb: 1.5, fontSize: 16, lineHeight: 1.65, textWrap: 'pretty' },
  '& p:last-child': { mb: 0 },
  '& h2, & h3, & h4': {
    fontFamily: DISPLAY,
    fontWeight: 700,
    fontSize: 18,
    lineHeight: 1.2,
    color: 'var(--tl-g9)',
    m: 0,
    mt: 2.5,
    mb: 1,
  },
  '& ul, & ol': { m: 0, mb: 1.5, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75 },
  '& li': { fontSize: 16, lineHeight: 1.6, textWrap: 'pretty' },
  '& li::marker': { color: 'var(--tl-teal)', fontWeight: 800 },
  '& strong': { color: 'var(--tl-g9)', fontWeight: 800 },
  '& a': { color: 'var(--tl-teal-d)', fontWeight: 700 },
  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 14,
    bgcolor: 'var(--tl-mint)',
    borderRadius: '6px',
    px: 0.75,
    py: 0.25,
  },
  // The privacy policy's three-column tables cannot fit a 390px phone —
  // `display: block` lets the table scroll sideways on its own instead of
  // pushing the whole page 48px wide. The inner rows and cells keep their
  // table display, so the columns still line up.
  '& table': {
    display: 'block',
    maxWidth: '100%',
    overflowX: 'auto',
    borderCollapse: 'collapse',
    fontSize: 15,
    mb: 1.5,
  },
  '& th, & td': { textAlign: 'left', p: '10px 12px', borderBottom: '1px solid #EEF3F1' },
  '& th': { color: 'var(--tl-g9)', fontWeight: 800 },
};

// ── one numbered section ──────────────────────────────────────────────
const Section = ({ section, vars, officer }) => {
  const html = useMemo(
    () => renderLegalMarkdown(section.content_md, vars),
    [section.content_md, vars],
  );
  // The privacy policy's last section embeds a marker where the grievance
  // card belongs, so the admin keeps writing plain markdown around it.
  const [before, after] = html.includes(GRIEVANCE_MARKER)
    ? html.split(GRIEVANCE_MARKER)
    : [html, null];

  return (
    <Box
      component="section"
      id={section.slug}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1.5, borderTop: '1px solid #EEF3F1', scrollMarginTop: '100px' }}
    >
      <Box component="span" sx={sectionTitle}>
        {sectionNumber(section.number)}. {section.title}
      </Box>
      <Box sx={prose} dangerouslySetInnerHTML={{ __html: before }} />
      {after !== null && officer && <GrievanceCard officer={officer} />}
      {after && <Box sx={prose} dangerouslySetInnerHTML={{ __html: after }} />}
    </Box>
  );
};

const GrievanceCard = ({ officer }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      my: 1.5,
      p: 2.5,
      borderRadius: '22px',
      bgcolor: 'var(--tl-g9)',
      color: '#FFFFFF',
      fontFamily: UI,
    }}
  >
    <Box component="span" sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--tl-teal-l)' }}>
      {officer.designation || 'Grievance officer'}
    </Box>
    <Box component="span" sx={{ fontSize: 18, fontWeight: 800 }}>{officer.name}</Box>
    {[
      ['Email', officer.email, `mailto:${officer.email}`],
      ['Phone', officer.phone, `tel:${String(officer.phone || '').replace(/\s/g, '')}`],
      ['Address', [officer.address, officer.pin].filter(Boolean).join(' — '), null],
      ['Hours', officer.working_hours, null],
    ].filter(([, v]) => v).map(([k, v, href]) => (
      <Box key={k} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, fontSize: 14, lineHeight: 1.6 }}>
        <Box component="span" sx={{ color: '#9FC2BA', minWidth: 64 }}>{k}</Box>
        {href ? (
          <Box component="a" href={href} sx={{ color: '#FFFFFF', fontWeight: 700 }}>{v}</Box>
        ) : (
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{v}</Box>
        )}
      </Box>
    ))}
  </Box>
);

const Legal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: privacy = {}, isLoading: privacyLoading } = useGetPrivacyPolicyQuery();
  const { data: terms = {}, isLoading: termsLoading } = useGetTermsOfServiceQuery();
  const { data: contact } = useGetContactInfoQuery();

  // The reference opens on the tab named in the hash, which is how the
  // footer's three links reach the right document.
  const hashTab = TABS.findIndex((t) => t.key === location.hash.replace('#', ''));
  const [tab, setTab] = useState(hashTab > 0 ? hashTab : 0);
  useEffect(() => {
    const i = TABS.findIndex((t) => t.key === location.hash.replace('#', ''));
    if (i >= 0) setTab(i);
  }, [location.hash]);

  const pickTab = (i) => {
    setTab(i);
    navigate(`#${TABS[i].key}`, { replace: true });
  };

  const parsedTerms = useMemo(() => splitTermsMarkdown(terms.md), [terms.md]);

  const vars = useMemo(() => ({
    company_name: privacy.company_name || 'TrustLab Diagnostics Pvt Ltd',
    registered_office: privacy.registered_office || '',
    cin: privacy.cin || '',
  }), [privacy.company_name, privacy.registered_office, privacy.cin]);

  const refundSection = parsedTerms.sections.find((s) => s.slug === REFUND_SECTION);

  const doc = (() => {
    if (tab === 1) {
      return {
        title: 'Terms of service',
        updated: terms.updated_at ? new Date(terms.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '',
        intro: parsedTerms.intro,
        sections: parsedTerms.sections,
        loading: termsLoading,
      };
    }
    if (tab === 2) {
      return {
        title: 'Refunds and cancellations',
        updated: terms.updated_at ? new Date(terms.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '',
        intro: '',
        sections: refundSection ? [{ ...refundSection, number: '1' }] : [],
        loading: termsLoading,
      };
    }
    return {
      title: [privacy.hero_title_line1, privacy.hero_title_line2].filter(Boolean).join(' ') || 'Privacy policy',
      updated: privacy.last_reviewed || privacy.effective_date || '',
      intro: privacy.hero_lede || '',
      sections: privacy.sections || [],
      loading: privacyLoading,
    };
  })();

  const email = contact?.email || 'customercare@mytrustlab.com';
  const phone = String(contact?.phone || '18005995656').replace(/\D/g, '');
  const phoneLabel = phone.length === 10 ? phone.replace(/(\d{5})(\d{5})/, '$1 $2')
    : phone.replace(/(\d{4})(\d{3})(\d{4})/, '$1 $2 $3');

  return (
    <Box
      component="main"
      sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      {/* ── title ──────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5, px: 1, pb: 0.5 }}>
        <Box
          component="span"
          sx={{ fontFamily: UI, fontSize: 13, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--tl-teal-d)' }}
        >
          Legal
        </Box>
        <Typography
          component="h1"
          sx={{
            m: 0,
            fontFamily: DISPLAY,
            fontWeight: 700,
            fontSize: 'clamp(36px, 4.6vw, 60px)',
            lineHeight: 0.95,
            letterSpacing: '-.035em',
            textWrap: 'balance',
            color: 'var(--tl-g9)',
          }}
        >
          {doc.title}
        </Typography>
        <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>
          {[
            doc.updated && `Last updated ${doc.updated}`,
            `Applies to ${vars.company_name}`,
            privacy.doc_id && tab === 0 ? `${privacy.doc_id} · v${privacy.version}` : null,
          ].filter(Boolean).join(' · ')}
        </Box>
      </Box>

      {/* ── tabs ───────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 0.5 }}>
        {TABS.map((t, i) => {
          const on = i === tab;
          return (
            <Box
              key={t.key}
              component="button"
              type="button"
              onClick={() => pickTab(i)}
              aria-pressed={on}
              sx={{
                border: 0,
                cursor: 'pointer',
                borderRadius: '999px',
                p: '10px 16px',
                whiteSpace: 'nowrap',
                fontFamily: UI,
                fontSize: 14,
                fontWeight: 700,
                bgcolor: on ? 'var(--tl-g9)' : '#FFFFFF',
                color: on ? '#FFFFFF' : 'var(--tl-g9)',
              }}
            >
              {t.label}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 3 }}>
        {/* ── the document ─────────────────────────────────────────── */}
        <Box component="article" sx={{ ...panel, flex: '2 1 560px', maxWidth: 820, gap: 1.5 }}>
          {/* The reference's refunds tab opens with a yellow summary. Ours
              summarises the policy that is actually published below it. */}
          {tab === 2 && refundSection && (
            <Box
              component="section"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                mb: 1.5,
                p: 'clamp(20px, 3.4vw, 32px)',
                borderRadius: '28px',
                bgcolor: 'var(--tl-yellow)',
                color: 'var(--tl-g9)',
              }}
            >
              <Box component="span" sx={sectionTitle}>In short</Box>
              <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75, fontFamily: UI, fontSize: 16, lineHeight: 1.6 }}>
                <li>Cancel free up to two hours before your slot starts.</li>
                <li>Inside two hours, half the amount is refunded — the Trustian is already on the way.</li>
                <li>Once the sample has been collected, it cannot be refunded.</li>
                <li>Refunds reach your original payment method within seven business days.</li>
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.55, color: '#3A4020' }}>
                This is a summary. The wording below, from our Terms of service, is what applies.
              </Box>
            </Box>
          )}

          {doc.intro && tab !== 2 && (
            <Box
              sx={{
                ...prose,
                pb: 0.5,
                '& p': { m: 0, mb: 1.5, fontSize: 17, lineHeight: 1.65, color: 'var(--tl-muted)' },
              }}
              dangerouslySetInnerHTML={{ __html: renderLegalMarkdown(doc.intro, vars) }}
            />
          )}

          {doc.loading && [0, 1, 2, 3].map((i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1.5, borderTop: '1px solid #EEF3F1' }}>
              <Box sx={{ height: 22, width: '52%', borderRadius: '6px', bgcolor: 'var(--tl-mint-2)' }} />
              <Box sx={{ height: 14, width: '100%', borderRadius: '6px', bgcolor: 'var(--tl-mint)' }} />
              <Box sx={{ height: 14, width: '88%', borderRadius: '6px', bgcolor: 'var(--tl-mint)' }} />
            </Box>
          ))}

          {!doc.loading && doc.sections.length === 0 && (
            <Box component="span" sx={{ fontFamily: UI, fontSize: 16, color: 'var(--tl-muted)', py: 1.5 }}>
              This document is not published yet. Write to{' '}
              <Box component="a" href={`mailto:${email}`} sx={{ color: 'var(--tl-teal-d)', fontWeight: 800 }}>{email}</Box>
              {' '}and we will send you a copy.
            </Box>
          )}

          {!doc.loading && doc.sections.map((s) => (
            <Section key={s.slug || s.number} section={s} vars={vars} officer={privacy.grievance_officer} />
          ))}

          {tab === 2 && refundSection && (
            <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: '#7D918C', pt: 1 }}>
              Taken from our Terms of service. Open the Terms tab for the full document.
            </Box>
          )}
        </Box>

        {/* ── aside ────────────────────────────────────────────────── */}
        <Box
          component="aside"
          sx={{ ...panel, flex: '1 1 260px', gap: 1, position: 'sticky', top: 100 }}
        >
          <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, color: 'var(--tl-g9)' }}>On this page</Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pb: 1.5, borderBottom: '1px solid #EEF3F1' }}>
            {doc.sections.map((s) => (
              <Box
                key={s.slug || s.number}
                component="a"
                href={`#${s.slug}`}
                sx={{
                  fontFamily: UI,
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: 'var(--tl-muted)',
                  textDecoration: 'none',
                  '&:hover': { color: 'var(--tl-teal-d)' },
                }}
              >
                {sectionNumber(s.number)}. {s.title}
              </Box>
            ))}
          </Box>

          <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, color: 'var(--tl-g9)', pt: 0.5 }}>
            Questions about this policy?
          </Box>
          <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: 'var(--tl-muted-2)' }}>
            Write to us and we&rsquo;ll reply within two working days.
          </Box>
          <Box component="a" href={`mailto:${email}`} sx={{ fontFamily: UI, fontWeight: 800, color: 'var(--tl-teal-d)', textDecoration: 'none' }}>
            {email}
          </Box>
          <Box component="a" href={`tel:${phone}`} sx={{ fontFamily: UI, fontWeight: 800, color: 'var(--tl-teal-d)', textDecoration: 'none' }}>
            {phoneLabel} (toll-free)
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Legal;
